import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AlertsRibbon } from "./AlertsRibbon";

interface HeaderProps {
  /** Mobile-only hamburger handler. On desktop the button is hidden so
   *  this is never called. */
  onToggleMobileNav?: () => void;
  onOpenAlerts?: () => void;
}

export function Header({ onToggleMobileNav, onOpenAlerts }: HeaderProps) {
  const drive = useQuery({
    queryKey: ["drive"],
    queryFn: api.drive,
    refetchInterval: 10_000,
  });

  const health = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 10_000,
  });

  const name = drive.data?.name ?? "—";
  const driveId = drive.data?.id ?? "—";
  const startDate = drive.data?.startDate ?? null;
  const endDate = drive.data?.endDate ?? null;
  const tz = drive.data?.shiftTimezone ?? "Europe/Dublin";

  // API health = ingest worker round-trip. Reflects "we can talk to HK",
  // not "HK is sending us data". A 200 with an empty payload still counts
  // as a successful fetch.
  const lastFetchAt = health.data?.lastFetchAt ?? null;
  const fetchAgoSec = lastFetchAt
    ? Math.max(0, Math.round((Date.now() - new Date(lastFetchAt).getTime()) / 1000))
    : null;
  // Stale-pill threshold: 3 minutes. Worker now ticks every 30 s
  // (config.ts FETCH_INTERVAL_MS default), so a healthy ingest will
  // never see a fetch_ago value above ~30 s. 180 s gives the worker
  // ~6 missed ticks of head-room before the pill flips — long enough
  // to ignore transient HK hiccups, short enough to surface a real
  // outage within a few minutes.
  const LIVE_THRESHOLD_S = 180;
  const apiHealthy =
    !!health.data?.ok && fetchAgoSec !== null && fetchAgoSec < LIVE_THRESHOLD_S;

  // Last data = newest ts in the readings table. Independent of API
  // health: HK can be reachable (apiHealthy=true) while not delivering
  // any rows, in which case this number stretches while the pill stays
  // green.
  const lastReadingAt = drive.data?.lastReadingAt ?? null;
  // NOT clamped to >= 0. A negative age means the newest stored reading
  // is dated in the FUTURE, which only happens when HK's timestamps are
  // converted wrongly — exactly what this indicator exists to catch.
  // Clamping it to zero rendered "0s ago" in healthy green while the data
  // was hours out of step with reality.
  const dataAgeSec = lastReadingAt
    ? Math.round((Date.now() - new Date(lastReadingAt).getTime()) / 1000)
    : null;

  return (
    <header className="relative flex items-center justify-between h-16 px-6 max-md:px-3 border-b border-border1 bg-bg">
      <div className="flex items-center gap-6 max-md:gap-3">
        {/* Hamburger — mobile only, opens the Sidebar drawer. */}
        <button
          type="button"
          onClick={onToggleMobileNav}
          aria-label="Open navigation"
          className="hidden max-md:inline-flex items-center justify-center h-9 w-9 -ml-1 rounded text-text hover:bg-surface2"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        <img src="/logo.png" alt="Ward & Burke" className="h-10 max-md:h-7" />
        <div className="h-8 w-px bg-border1 max-md:hidden" />
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-xs tracking-[0.2em] text-muted uppercase max-md:hidden">TBM Data Portal</span>
          <span className="text-[15px] font-semibold text-text max-md:text-[13px] max-md:truncate">{name}</span>
        </div>
      </div>

      <div className="flex items-center gap-5 max-md:gap-3">
        {/* Control container chip — desktop only; visible from md upward. */}
        <div className="flex items-center gap-2 text-xs max-md:hidden">
          <span className="text-muted uppercase tracking-wider">Control container</span>
          <span className="mono-nums text-text">{driveId}</span>
        </div>
        <div className="h-6 w-px bg-border1 max-md:hidden" />
        <AlertsRibbon onOpen={() => onOpenAlerts?.()} />
        {endDate && (
          <>
            <div className="h-6 w-px bg-border1 max-md:hidden" />
            <NotLiveIndicator startDate={startDate} endDate={endDate} tz={tz} />
          </>
        )}
        <div className="h-6 w-px bg-border1 max-md:hidden" />
        <ApiHealthIndicator healthy={apiHealthy} fetchAgoSec={fetchAgoSec} />
        <div className="h-6 w-px bg-border1 max-md:hidden" />
        <LastDataIndicator ageSec={dataAgeSec} lastReadingAt={lastReadingAt} />
      </div>

      <div className="absolute inset-x-0 -bottom-px hairline" />
    </header>
  );
}

function NotLiveIndicator({
  startDate,
  endDate,
  tz,
}: {
  startDate: string | null;
  endDate: string;
  tz: string;
}) {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", {
      timeZone: tz,
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  const range = `${startDate ? fmt(startDate) : "—"} → ${fmt(endDate)}`;
  return (
    <div className="flex items-baseline gap-2 max-md:gap-1.5" title={`Not live — bounded drive, ${range}`}>
      <span className="text-[10px] uppercase tracking-[0.18em] text-warn">Not live</span>
      <span className="text-[11px] text-muted max-md:hidden">{range}</span>
    </div>
  );
}

function ApiHealthIndicator({
  healthy,
  fetchAgoSec,
}: {
  healthy: boolean;
  fetchAgoSec: number | null;
}) {
  const lagLabel =
    fetchAgoSec === null
      ? "no fetch yet"
      : `last fetch ${fmtAge(fetchAgoSec)} ago`;
  return (
    <div
      className="flex items-baseline gap-2 max-md:gap-1.5"
      title={`API health: ${healthy ? "OK" : "stale"} — ${lagLabel}. Reflects worker round-trip to HK, NOT whether data was returned.`}
    >
      <span className="text-[10px] uppercase tracking-[0.18em] text-dim max-md:hidden">API health</span>
      <span className="flex items-center gap-1.5">
        <span
          className={
            healthy
              ? "h-2 w-2 rounded-full bg-ok animate-pulseLive"
              : "h-2 w-2 rounded-full bg-dim"
          }
        />
        <span
          className={
            healthy
              ? "text-xs font-medium tracking-wider uppercase text-ok"
              : "text-xs font-medium tracking-wider uppercase text-dim"
          }
        >
          {healthy ? "OK" : "Stale"}
        </span>
      </span>
      {/* Lag label takes too much room on a phone; hide and rely on the title attribute. */}
      <span className="text-[11px] text-muted max-md:hidden">{lagLabel}</span>
    </div>
  );
}

function LastDataIndicator({
  ageSec,
  lastReadingAt,
}: {
  ageSec: number | null;
  lastReadingAt: string | null;
}) {
  const ahead = ageSec !== null && ageSec < -60;
  const ageLabel =
    ageSec === null
      ? "no data yet"
      : ahead
        ? `${fmtAge(Math.abs(ageSec))} ahead`
        : `${fmtAge(Math.max(0, ageSec))} ago`;
  // Tone the time-ago string by how stale the data is. The thresholds
  // are intentionally tight because most HK sensors push at least once
  // a minute when the machine is on; anything older than ~5 min while
  // the operator is tunnelling means HK isn't shipping.
  let toneClass = "text-text";
  if (ageSec === null) {
    toneClass = "text-dim";
  } else if (ahead) {
    toneClass = "text-wb";        // future-dated: HK timestamps converted wrongly
  } else if (ageSec > 60 * 60) {
    toneClass = "text-wb";        // > 1 h: loud
  } else if (ageSec > 5 * 60) {
    toneClass = "text-warn";      // 5 min – 1 h: warning
  } else if (ageSec > 60) {
    toneClass = "text-muted";     // 1 – 5 min: muted
  }
  return (
    <div
      className="flex items-baseline gap-2 max-md:gap-1.5"
      title={
        lastReadingAt
          ? ahead
            ? `Last reading stored in DB at ${lastReadingAt}, which is in the FUTURE. ` +
              `HK timestamps are not being converted correctly; see /api/debug/timeline-check.`
            : `Last reading stored in DB at ${lastReadingAt}. Reflects newest row in the readings table, regardless of API health.`
          : "No data has ever been stored."
      }
    >
      <span className="text-[10px] uppercase tracking-[0.18em] text-dim max-md:hidden">Last data</span>
      <span className={`mono-nums text-xs font-medium ${toneClass}`}>{ageLabel}</span>
    </div>
  );
}

function fmtAge(s: number): string {
  if (s < 60) return `${s}s`;
  const MIN = 60, HOUR = 3600, DAY = 86400, WEEK = 604800, YEAR = 365 * DAY;
  let rem = s;
  const y = Math.floor(rem / YEAR); rem -= y * YEAR;
  const w = Math.floor(rem / WEEK); rem -= w * WEEK;
  const d = Math.floor(rem / DAY);  rem -= d * DAY;
  const h = Math.floor(rem / HOUR); rem -= h * HOUR;
  const m = Math.floor(rem / MIN);
  // Build largest → smallest, dropping any zero component (e.g. "1w 2d",
  // "3h 5m", "1y 4w 2d") so we never print "0 years".
  const parts: string[] = [];
  if (y) parts.push(`${y}y`);
  if (w) parts.push(`${w}w`);
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.length > 0 ? parts.join(" ") : "0m";
}

